import React, { useEffect, useState } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const PermitBuilder = ({ isMobile }) => {
    const [permits, setPermits] = useState([]);
    const [roles, setRoles] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        name: '', description: '', questions: [], approverCount: 1, 
        approval_chain: [] 
    });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try { 
            // Fetch Permits AND Roles simultaneously
            const [pRes, rRes] = await Promise.all([
                api.get('config/permits/'),
                api.get('config/roles-options/')
            ]);
            
            setPermits(pRes.data);
            setRoles(rRes.data);

            // Initialize default chain with first available role (if any)
            if (rRes.data.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    approval_chain: [{ role: rRes.data[0].code, label: 'Safety Sign-off' }]
                }));
            }
        } 
        catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- LOGIC: QUESTIONS & CHAIN ---
    
    // Updated: Defaults new questions to condition 'Yes'
    const addQuestion = () => setFormData({ 
        ...formData, 
        questions: [
            ...formData.questions, 
            { 
                id: Date.now(), 
                text: '', 
                type: 'checkbox', // This will now represent a Yes/No toggle in the UI
                showIf: null, 
                showIfCondition: 'Yes' // Default condition
            }
        ] 
    });

    const updateQuestion = (idx, field, val) => { 
        const n = [...formData.questions]; 
        n[idx][field] = val; 
        setFormData({ ...formData, questions: n }); 
    };

    // NEW: Handles setting both the ID and the Condition from the single dropdown
    const handleLogicChange = (idx, valueString) => {
        const n = [...formData.questions];
        
        if (!valueString) {
            // User selected (Always Visible)
            n[idx].showIf = null;
            n[idx].showIfCondition = 'Yes'; // Reset to default
        } else {
            // Parse "ID:Condition" string
            const [targetId, condition] = valueString.split(':');
            n[idx].showIf = parseInt(targetId);
            n[idx].showIfCondition = condition;
        }
        
        setFormData({ ...formData, questions: n });
    };
    
    const removeQuestion = (idx) => { 
        const n = formData.questions.filter((_, i) => i !== idx); 
        setFormData({ ...formData, questions: n }); 
    };

    const handleChainChange = (idx, field, val) => { const n = [...formData.approval_chain]; n[idx][field] = val; setFormData({...formData, approval_chain: n}); };
    
    const handleApproverCountChange = (count) => {
        const c = parseInt(count) || 0;
        let chain = [...formData.approval_chain];
        
        // Fallback default role if adding new steps
        const defaultRoleCode = roles.length > 0 ? roles[0].code : 'SAFE_ENG';

        if (c > chain.length) for(let i=0; i < c - chain.length; i++) chain.push({ role: defaultRoleCode, label: '' });
        else chain = chain.slice(0, c);
        setFormData({ ...formData, approverCount: c, approval_chain: chain });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanQuestions = formData.questions.filter(q => q.text && q.text.trim() !== '');
        try {
            const payload = { ...formData, questions: cleanQuestions };
            if (editId) await api.put(`config/permits/${editId}/`, payload);
            else await api.post('config/permits/', payload);
            alert("✅ Saved"); resetForm(); 
            // Reload permits to show update
            const res = await api.get('config/permits/');
            setPermits(res.data);
        } catch (err) { alert("Error saving"); }
    };

    const handleEdit = (p) => {
        // Ensure legacy data has IDs, object structure, AND the new showIfCondition
        const qs = (p.questions || []).map((q, i) => {
            const base = typeof q === 'string' ? { id: Date.now()+i, text: q, type: 'checkbox' } : q;
            return {
                ...base,
                showIf: base.showIf || null,
                showIfCondition: base.showIfCondition || 'Yes' // Default old templates to 'Yes'
            };
        });
        
        // Ensure chain exists
        const defaultRoleCode = roles.length > 0 ? roles[0].code : 'SAFE_ENG';
        const chain = (p.approval_chain && p.approval_chain.length > 0) 
            ? p.approval_chain 
            : [{ role: defaultRoleCode, label: 'Safety Sign-off' }];

        setFormData({ ...p, questions: qs, approverCount: chain.length, approval_chain: chain });
        setEditId(p.id); setIsEditing(true);
        // On mobile, scroll to top to see form
        if (isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => { if(window.confirm("Delete?")) { await api.delete(`config/permits/${id}/`); loadData(); }};
    
    const resetForm = () => { 
        const defaultRoleCode = roles.length > 0 ? roles[0].code : 'SAFE_ENG';
        setFormData({ name: '', description: '', questions: [], approverCount: 1, approval_chain: [{ role: defaultRoleCode, label: 'Safety Sign-off' }] }); 
        setIsEditing(false); setEditId(null); 
    };

    const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', fontSize: '0.9rem', border: `1px solid ${THEME.border}`, background: '#020617', color: 'white', boxSizing: 'border-box' };

    if (loading) return <div style={{color:'white'}}>Loading...</div>;

    return (
        <div className="animate-fade-in" style={{
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr', // Stack on mobile
            gap: '30px'
        }}>
            
            {/* LEFT (or TOP): FORM */}
            <div style={{background: THEME.card, padding: '25px', borderRadius: '12px', border: `1px solid ${THEME.border}`}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                    <h3 style={{margin:0, color:'white'}}>{isEditing ? '✏️ Edit Permit Template' : '➕ New Permit Template'}</h3>
                    {isEditing && <button onClick={resetForm} style={{background:'transparent', color: THEME.textSec, border: `1px solid ${THEME.border}`, padding:'6px 12px', borderRadius:'4px', cursor:'pointer'}}>Cancel</button>}
                </div>

                <form onSubmit={handleSubmit} style={{display:'flex', flexDirection:'column', gap:'20px'}}>
                    <div>
                        <label style={{display:'block', color: THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>Permit Name</label>
                        <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={inputStyle} placeholder="e.g. Hot Work Permit" required />
                    </div>
                    <div>
                        <label style={{display:'block', color: THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>Instructions</label>
                        <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} style={{...inputStyle, height:'80px'}} placeholder="Brief instructions for the applicant..." />
                    </div>

                    {/* APPROVAL CHAIN */}
                    <div style={{background: '#0f172a', padding:'15px', borderRadius:'8px', border: `1px dashed ${THEME.warning}`}}>
                        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                            <h4 style={{margin:0, color: THEME.warning, fontSize:'0.95rem'}}>🛡️ Approval Chain</h4>
                            <input type="number" min="1" max="5" value={formData.approverCount} onChange={e => handleApproverCountChange(e.target.value)} style={{width:'50px', padding:'4px', background: THEME.card, border: `1px solid ${THEME.border}`, color:'white', borderRadius:'4px'}} />
                        </div>
                        <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                            {formData.approval_chain.map((step, i) => (
                                <div key={i} style={{display:'flex', gap:'10px', alignItems:'center', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center'}}>
                                    <span style={{color: THEME.textSec, fontSize:'0.8rem', width: isMobile ? 'auto' : '20px'}}>{i+1}.</span>
                                    <select value={step.role} onChange={e => handleChainChange(i, 'role', e.target.value)} style={{...inputStyle, flex:1, padding:'8px'}}>
                                        {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                                    </select>
                                    <input value={step.label} onChange={e => handleChainChange(i, 'label', e.target.value)} placeholder="Label (e.g. Safety)" style={{...inputStyle, flex:1, padding:'8px'}} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* QUESTIONS WITH LOGIC JUMPS */}
                    <div style={{background: '#0f172a', padding:'15px', borderRadius:'8px', border: `1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color: THEME.primary, fontSize:'0.95rem'}}>✅ Safety Checklist</h4>
                        
                        {formData.questions.map((q, idx) => (
                            <div key={q.id || idx} style={{marginBottom:'15px', padding:'15px', background:'rgba(255,255,255,0.03)', borderRadius:'6px', border: `1px solid ${THEME.border}`}}>
                                
                                {/* Row 1: Question Text & Delete */}
                                <div style={{display:'flex', gap:'10px', alignItems:'center', marginBottom:'10px'}}>
                                    <span style={{color: THEME.textSec, fontSize:'0.8rem'}}>Q{idx+1}</span>
                                    <input 
                                        value={q.text} 
                                        onChange={e => updateQuestion(idx, 'text', e.target.value)} 
                                        placeholder="Question text..." 
                                        style={{...inputStyle, padding:'8px'}} 
                                    />
                                    <button type="button" onClick={() => removeQuestion(idx)} style={{background: 'transparent', border:'none', color: THEME.danger, cursor:'pointer', fontSize:'1.2rem'}}>✕</button>
                                </div>

                                {/* Row 2: Type & Logic Jump (Stacked on Mobile) */}
                                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr', gap:'10px'}}>
                                    <div>
                                        <label style={{color:'#64748b', fontSize:'0.75rem', display:'block', marginBottom:'2px'}}>Response Type</label>
                                        <select 
                                            value={q.type} 
                                            onChange={e => updateQuestion(idx, 'type', e.target.value)} 
                                            style={{...inputStyle, padding:'6px', fontSize:'0.85rem'}}
                                        >
                                            <option value="checkbox">Yes/No Toggle</option>
                                            <option value="text">Text Input</option>
                                        </select>
                                    </div>
                                    
                                    {/* LOGIC JUMP SELECTOR (NEW) */}
                                    <div>
                                        <label style={{color:'#64748b', fontSize:'0.75rem', display:'block', marginBottom:'2px'}}>Show only if:</label>
                                        <select 
                                            // Value format: "ID:Condition" (e.g. "12345:No")
                                            value={q.showIf ? `${q.showIf}:${q.showIfCondition || 'Yes'}` : ''}
                                            onChange={(e) => handleLogicChange(idx, e.target.value)}
                                            style={{
                                                ...inputStyle, 
                                                padding:'6px', 
                                                fontSize:'0.85rem',
                                                border: q.showIf ? `1px solid ${THEME.warning}` : `1px solid ${THEME.border}`,
                                                color: q.showIf ? THEME.warning : 'white'
                                            }}
                                        >
                                            <option value="">(Always Visible)</option>
                                            
                                            {/* Logic: Loop previous items and offer Yes OR No triggers */}
                                            {formData.questions
                                                .slice(0, idx)
                                                .filter(prev => prev.type === 'checkbox')
                                                .map(prev => (
                                                    <optgroup key={prev.id} label={`Q${formData.questions.indexOf(prev)+1}: ${prev.text.substring(0, 20)}...`}>
                                                        <option value={`${prev.id}:Yes`}>Answer is YES</option>
                                                        <option value={`${prev.id}:No`}>Answer is NO</option>
                                                    </optgroup>
                                                ))
                                            }
                                        </select>
                                    </div>
                                </div>

                            </div>
                        ))}
                        <button type="button" onClick={addQuestion} style={{width:'100%', padding:'8px', background:'transparent', border: `1px dashed ${THEME.primary}`, color: THEME.primary, borderRadius:'6px', cursor:'pointer', marginTop:'10px'}}>+ Add Question</button>
                    </div>

                    <button type="submit" style={{width:'100%', padding:'12px', background: THEME.success, color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer'}}>
                        {isEditing ? 'Save Changes' : 'Create Template'}
                    </button>
                </form>
            </div>

            {/* RIGHT (or BOTTOM): LIST */}
            <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                <h3 style={{color:'white', margin:0, fontSize:'1.1rem'}}>Existing Templates</h3>
                {permits.length === 0 && <div style={{color:THEME.textSec, fontStyle:'italic'}}>No templates found.</div>}
                
                {permits.map(p => (
                    <div key={p.id} style={{background: THEME.card, padding:'20px', borderRadius:'12px', border: `1px solid ${THEME.border}`, position:'relative'}}>
                        <h4 style={{margin:'0 0 5px', color:'white'}}>{p.name}</h4>
                        <div style={{fontSize:'0.85rem', color: THEME.textSec}}>{p.approval_chain?.length || 0} Approvers • {p.questions?.length || 0} Questions</div>
                        <div style={{marginTop:'15px', display:'flex', gap:'10px'}}>
                            <button onClick={() => handleEdit(p)} style={{flex:1, padding:'6px', background:'transparent', border: `1px solid ${THEME.border}`, color: 'white', borderRadius:'4px', cursor:'pointer'}}>Edit</button>
                            <button onClick={() => handleDelete(p.id)} style={{padding:'6px', background:'transparent', border: `1px solid ${THEME.danger}`, color: THEME.danger, borderRadius:'4px', cursor:'pointer'}}>Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PermitBuilder;